import type { ComponentType } from 'react';
import { ArrowLeftRight, Settings, UserRound } from 'lucide-react';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { Link } from 'react-router-dom';
import {
  CardIcon,
  CashbackIcon,
  CustomerRequestIcon,
  DashboardIcon,
  TransactionIcon,
  WalletIcon,
} from '@/components/Sidebar/icon';

type SidebarIcon = ComponentType<{ className?: string }>;

const sidebarIconMap: Record<string, SidebarIcon> = {
  'dashboard-sidebar': DashboardIcon,
  'virtual-account-sidebar': WalletIcon,
  'customer-request-sidebar': CustomerRequestIcon,
  'card-sidebar': CardIcon,
  'transaction-sidebar': TransactionIcon,
  'fx-sidebar': ArrowLeftRight,
  'cashback-sidebar': CashbackIcon,
  'user-sidebar': UserRound,
  'settings-sidebar': Settings,
};

const getIconComponent = (icon?: string | SidebarIcon): SidebarIcon => {
  if (typeof icon === 'function') return icon;
  if (!icon) return DashboardIcon;
  return sidebarIconMap[icon] ?? DashboardIcon;
};

type MainSideBarProps = {
  isCollapsed: boolean;
  menuItems: MenuItem[];
  isActive: (route?: string) => boolean;
  setCurrentView: (view: 'MAIN' | 'SETTINGS') => void;
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
};

const MainSideBar = ({
  isCollapsed,
  menuItems,
  isActive,
  setCurrentView,
  isMobile,
  setOpenMobile,
}: MainSideBarProps) => {
  return (
    <>
      <SidebarMenu className={`flex flex-col px-[1.85rem] ${isCollapsed ? 'mt-[2rem] items-center gap-[2.8rem] !overflow-visible' : 'gap-[1.2rem]'}`}>
        {menuItems.map((menu, index) => (
          <div key={index}>
            <p className={`${isCollapsed ? 'hidden' : 'ml-[1rem]'} font-normal text-[1.3rem] text-disable leading-[120%] tracking-[0] align-middle`}>
              {menu.label}
            </p>
            <div
              className={`${isCollapsed ? 'mt-0' : 'mt-[1.6rem]'}`}>
              {menu.type === 'Section' && (
                <>
                  {menu.children?.map((item, index) => {
                    // Honour the data's enabled flag (legacy API already ships Profile as enabled=false;
                    // the mock menu marks not-yet-wired modules the same way).
                    // Settings opens the local drilldown even when the legacy
                    // menu endpoint marks it as disabled.
                    const isSettings = item.label === 'Settings';
                    const isDisabled = !isSettings && item.enabled === false;
                    const Icon = getIconComponent(item.icon);

                    return (
                      <SidebarMenuItem key={index}>
                        <SidebarMenuButton
                          asChild
                          aria-disabled={isDisabled}
                          disabled={isDisabled}
                          tooltip={isCollapsed ? item.label : undefined}
                          className={`text-[1.5rem] family-[montserrat] md:px-[1.2rem] ${isCollapsed ? '!h-[4.4rem] !w-[4.4rem] !rounded-[1rem] !p-0' : 'rounded-[1.2rem]'
                            } ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          {isDisabled ? (
                            <span
                              className={`group/item flex h-[5rem] w-full dark:!bg-transparent !bg-[#FFFFFF] ${isCollapsed ? 'justify-center items-center' : 'gap-[1rem]'}`}
                            >
                              {/* ICON */}
                              <div
                                className={`icon flex shrink-0 items-center justify-center ${isCollapsed ? 'h-[1.8rem] w-[1.8rem]' : 'h-[2rem] w-[2rem]'}`}
                              >
                                {item.icon && (
                                  <div className="transition-all ease duration-300">
                                    <Icon
                                      className={`!h-full !w-full transition-all ease duration-300 ${isActive(item.route) ? 'text-main' : 'text-second'}`}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* TITLE */}
                              <span className={`${isCollapsed ? 'hidden' : ''} font-semibold text-[1.5rem] leading-none tracking-[0] align-middle ${isActive(item.route) ? 'text-main' : 'text-second'}`}>
                                {item.label}
                              </span>
                            </span>
                          ) : (
                            <Link
                              to={item.route || '#'}
                              className={`group/item flex h-[5rem] w-full ${isActive(item.route)
                                ? '!bg-interactive'
                                : 'dark:bg-transparent'
                                } ${isCollapsed ? 'justify-center items-center' : 'gap-[1rem]'}`}
                              onClick={(e) => {
                                if (item.label === 'Settings') {
                                  e.preventDefault();
                                  setCurrentView('SETTINGS');
                                  return;
                                }

                                if (isMobile) {
                                  setOpenMobile(false);
                                }
                              }}
                            >
                              {/* ICON */}
                              <div
                                className={`icon flex shrink-0 items-center justify-center ${isCollapsed ? 'h-[1.8rem] w-[1.8rem]' : 'h-[2rem] w-[2rem]'}`}
                              >
                                {item.icon && (
                                  <div className="transition-all ease duration-300">
                                    <Icon
                                      className={`!h-full !w-full transition-all ease duration-300 ${isActive(item.route) ? 'text-main' : 'text-second'}`}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* TITLE */}
                              <span
                                className={`${isCollapsed ? 'hidden' : ''} font-semibold text-[1.5rem] leading-none tracking-[0] align-middle ${isActive(item.route) ? 'text-main' : 'text-second'}`}
                              >
                                {item.label}
                              </span>
                            </Link>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        ))}
      </SidebarMenu>
    </>
  );
};

export default MainSideBar;
